import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db, analytics } from '../../../firebase';
import { apiCompleteProviderSetup } from '../../../rms/utils/rmsApi';
import { signOut } from 'firebase/auth';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
// logEvent also feeds the first-party stream via the GA4 bridge;
// the signature and GA4 behaviour are unchanged.
import { logEvent } from '../../../analytics/ga4';
import InfoTooltip from '../../../components/InfoTooltip';
import OtpInput from '../../../components/auth/OtpInput';
import TotpEnrollmentPanel from '../../../components/auth/TotpEnrollmentPanel';
import MfaMethodSelector from '../../../components/auth/MfaMethodSelector';
import { getBackgroundGradient, isNonProdEnvironment } from '../../../utils/environmentTheme';
import { RMS_PROVIDER_USER_TYPES } from '../../../constants/userTypes';
import useMfaEnrollment from '../../../hooks/auth/useMfaEnrollment';

// Post-enrolment landing page per product.
const dashboardPathFor = (userType) =>
    RMS_PROVIDER_USER_TYPES.includes(userType) ? '/rms/provider/dashboard' : '/dashboard';

/**
 * SetUpMFA (provider first-time enrollment)
 * ========================================
 * Mandatory MFA setup for new clinician / RMS provider accounts. There is no
 * "Skip" option here (unlike the optional service-user flow) — only sign out.
 *
 * Environment split (why this page is env-aware)
 * ----------------------------------------------
 * - Production (all production Firebase tenants):
 *   authenticator-app (TOTP) ONLY. SMS enrollment was removed for reliability
 *   (carrier filtering, poor signal, reCAPTCHA failures, regional allowlists all
 *   caused failed setups and high support load). Authenticator apps generate
 *   codes offline and are far more robust.
 * - Dev/staging (non-production Firebase projects): providers
 *   are shown BOTH methods (Authenticator recommended, or SMS) so training/test
 *   accounts can enroll Firebase *test phone numbers* (fixed codes, no real SMS).
 *   Setting up authenticator apps for every staging provider would be a large,
 *   pointless burden for QA/training.
 *
 * The gate is [`isNonProdEnvironment()`], the same allowlist (`development` |
 * `staging`) used across the app — it correctly treats BOTH production tenants
 * (portal and SLaM NHS) as production.
 *
 * Existing SMS-enrolled providers are unaffected: this page is only for *new*
 * enrollment. Sign-in (which still accepts SMS) and the Settings SMS->TOTP
 * switch are separate flows and untouched.
 *
 * All enrollment mechanics live in the shared `useMfaEnrollment` hook, shared
 * with the service-user page so the two never drift. This page owns the
 * provider-specific bits: which Firestore doc to write, RMS onboarding
 * finalisation, sign-out routing, and the env-gated UI.
 */
export default function SetUpMFA() {
    const navigate = useNavigate();

    // For analytics consistency + support email + doc resolution.
    const [supportOrg, setSupportOrg] = useState('');
    const [accountType, setAccountType] = useState('Provider');
    const [userType, setUserType] = useState('');

    // In dev/staging providers may choose Authenticator or SMS; in production
    // the page is authenticator-only.
    const allowSms = isNonProdEnvironment();

    // Fetch organisation claim for support emails + identity (best-effort).
    useEffect(() => {
        const u = auth?.currentUser;
        if (!u) return;
        try {
            u.getIdTokenResult(true).then((res) => {
                // Assessment providers carry `organisation`; RMS providers carry
                // `rmsOrgId` (post-#90). Accept either so the org resolves for
                // both products (TD-31).
                const org = res?.claims?.organisation || res?.claims?.rmsOrgId || '';
                setSupportOrg(org);
                try {
                    if (res?.claims?.accountType) setAccountType(res.claims.accountType);
                    if (res?.claims?.userType) setUserType(res.claims.userType);
                } catch (_) {}
            }).catch(() => {});
        } catch (_) {}
    }, []);

    // -------------------------------------------------------------------------
    // Resolve the correct Firestore document ref for persisting MFA status.
    //
    // ADHD Assessment providers use the global `clinicians/{uid}` collection
    // keyed by Firebase Auth UID. RMS provider users live under the org-scoped
    // `rms_organisations/{rmsOrgId}/rmsProviderUsers/{RPU-xxx}` subcollection,
    // where the document ID is a prefixed UUID — NOT the UID. We locate the right
    // doc by querying on the `accountId` field (TD-31).
    //
    // Returns { ref, snap } where `ref` is the DocumentReference and `snap`
    // is the existing DocumentSnapshot (used to check for prior mfaSetupAt).
    // Returns null if the user's profile document cannot be found.
    // -------------------------------------------------------------------------
    const resolveProviderDoc = useCallback(async (uid) => {
        if (RMS_PROVIDER_USER_TYPES.includes(userType) && supportOrg) {
            const q = query(
                collection(db, 'rms_organisations', supportOrg, 'rmsProviderUsers'),
                where('accountId', '==', uid)
            );
            const snap = await getDocs(q);
            if (!snap.empty) {
                return { ref: snap.docs[0].ref, snap: snap.docs[0] };
            }
            console.warn('[MFA] resolveProviderDoc: no rmsProviderUsers doc found for uid', uid);
            return null;
        }
        const ref = doc(db, 'clinicians', uid);
        const snap = await getDoc(ref);
        return { ref, snap };
    }, [userType, supportOrg]);

    // The doc-resolver the hook calls (no args — resolves the current user).
    const resolveDocRef = useCallback(async () => {
        const uid = auth.currentUser?.uid;
        if (!uid) return null;
        return resolveProviderDoc(uid);
    }, [resolveProviderDoc]);

    // -------------------------------------------------------------------------
    // RMS onboarding finalisation.
    //
    // For RMS provider users, the profile transitions 'invited' -> 'active' (and
    // the invite is consumed) via rmsCompleteProviderSetup, which verifies the
    // enrolled second factor server-side. Calling it here, straight after
    // enrolment succeeds, means completion doesn't depend on a later sign-in.
    // Failures are non-blocking: the call is idempotent and the sign-in and
    // dashboard paths retry it, so we log and continue rather than trap the
    // user on this page. No-op for Assessment providers.
    // -------------------------------------------------------------------------
    const finalizeRmsProviderSetup = useCallback(async () => {
        let resolvedType = userType;
        try {
            let orgId = supportOrg;
            if (!resolvedType || !orgId) {
                const res = await auth.currentUser?.getIdTokenResult();
                resolvedType = resolvedType || res?.claims?.userType || '';
                orgId = orgId || res?.claims?.rmsOrgId || '';
            }
            if (!RMS_PROVIDER_USER_TYPES.includes(resolvedType) || !orgId) return resolvedType;
            await apiCompleteProviderSetup({ orgId });
            logEvent(analytics, 'rms_provider_setup_finalized', { userType: resolvedType });
        } catch (err) {
            console.warn('[MFA] rmsCompleteProviderSetup failed (non-blocking):', err);
            logEvent(analytics, 'rms_provider_setup_finalize_failed', {
                code: err?.code || 'unknown',
                userType: resolvedType,
            });
        }
        return resolvedType;
    }, [userType, supportOrg]);

    // Single exit point for every enrolment success path: finalise RMS
    // onboarding (no-op for Assessment providers), then route to the product
    // dashboard using the freshest identity available.
    const finalizeAndGoToDashboard = useCallback(async () => {
        const resolvedType = await finalizeRmsProviderSetup();
        navigate(dashboardPathFor(resolvedType || userType));
    }, [finalizeRmsProviderSetup, userType, navigate]);

    // Shared enrollment engine. In production, seed the method to 'totp' so the
    // authenticator panel renders (and auto-generates) immediately with no
    // picker. In dev/staging, start with no method so the picker is shown.
    const enrollment = useMfaEnrollment({
        allowSms,
        initialMethod: allowSms ? null : 'totp',
        resolveDocRef,
        onEnrolled: finalizeAndGoToDashboard,
        analytics: { accountType, userType, eventPrefix: '', logFailures: true },
    });

    const {
        selectedMethod,
        handleSelectMethod,
        handleChangeMethod,
        isMethodLocked,
        error,
        errorCode,
        verificationCode,
        setVerificationCode,
        panelContentRef,
        panelHeight,
        phoneNumber,
        isPhoneValid,
        handlePhoneChange,
        isCodeSent,
        isSending,
        handleSendCode,
        handleResendCode,
        handleVerifySms,
        resendSecondsLeft,
        isResending,
        resendJustSent,
        last4,
        totpUri,
        totpSecretKey,
        isTotpReady,
        isTotpError,
        isTotpEnrolling,
        copiedSecret,
        handleVerifyTotpCode,
        handleCopySecret,
        handleRetryTotp,
    } = enrollment;

    const handleSignOut = async () => {
        const loginPage = RMS_PROVIDER_USER_TYPES.includes(userType) ? '/rms/provider-sign-in' : '/login';
        try {
            enrollment.cleanupRecaptcha();
            await signOut(auth);
            navigate(loginPage);
        } catch (err) {
            console.error('Error signing out:', err);
            navigate(loginPage);
        }
    };

    // Ensure consistent vertical spacing at the top of the panel: in dev/staging
    // we render an environment banner that provides top space; in production (no
    // banner), we add top margin to the logo so it doesn't touch the card edge.
    const showEnvBanner =
        import.meta.env.VITE_ENVIRONMENT === 'development' || import.meta.env.VITE_ENVIRONMENT === 'staging';

    // --- Enrollment sub-panels (kept as local render helpers so the authenticator
    //     block is written once and reused by both the production-only path and
    //     the dev/staging focused-panel path) ------------------------------------

    // Authenticator-app (TOTP) enrollment UI. The retry prompt owns the
    // generation-failure case, so we only render the panel while there's no error.
    const renderTotpEnrollment = () => (
        <>
            {!isTotpError && (
                <TotpEnrollmentPanel
                    isReady={isTotpReady}
                    totpUri={totpUri}
                    totpSecretKey={totpSecretKey}
                    code={verificationCode}
                    onCodeChange={setVerificationCode}
                    otpDisabled={isTotpEnrolling}
                    copied={copiedSecret}
                    onCopySecret={handleCopySecret}
                    qrSize={180}
                    introText="Scan the QR code below with your authenticator app, then enter the 6-digit code it generates."
                    showAppGuidance
                >
                    <button
                        onClick={handleVerifyTotpCode}
                        disabled={(verificationCode || '').length !== 6 || isTotpEnrolling}
                        className={`w-full font-semibold py-2 rounded-lg transition duration-100 ${(verificationCode || '').length !== 6 || isTotpEnrolling ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-primary text-white hover:bg-primary/90'}`}
                    >
                        {isTotpEnrolling ? (
                            <div className="flex items-center justify-center">
                                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                                Verifying...
                            </div>
                        ) : (
                            'Verify & Complete Setup'
                        )}
                    </button>
                </TotpEnrollmentPanel>
            )}

            {/* Error state when TOTP generation failed. */}
            {!isTotpReady && isTotpError && (
                <div className="text-center py-4">
                    <p className="text-sm text-gray-500 mb-3">Could not generate authenticator setup.</p>
                    <button
                        type="button"
                        onClick={handleRetryTotp}
                        className="text-sm font-medium text-primary hover:text-primary/80 underline"
                    >
                        Try again
                    </button>
                </div>
            )}
        </>
    );

    // SMS enrollment UI (dev/staging only): phone entry -> code entry.
    const renderSmsEnrollment = () => (
        <div className="animate-fadeIn">
            <div className="text-gray-600 text-sm mb-4 text-center">
                {!isCodeSent ? (
                    <p>Enter your mobile number to receive a verification code via SMS.</p>
                ) : (
                    <p>{`A 6-digit code has been sent to your mobile phone${last4 ? ` ending in ••••${last4}` : ''}. Enter it below.`}</p>
                )}
            </div>

            {!isCodeSent ? (
                <div>
                    <div className="mb-4">
                        <div className="flex items-center justify-between mb-1">
                            <label className="block text-xs font-bold text-gray-700 text-left">Mobile Phone Number</label>
                            <InfoTooltip side="right" content={'We currently only support UK phone numbers.'} />
                        </div>
                        <div className="flex items-center gap-2 w-full">
                            <div className="shrink-0 inline-flex items-center gap-2 px-3 py-2 border rounded-md bg-white text-gray-800">
                                <img src="/gb_flag.svg" alt="GB" className="h-4 w-auto" />
                                <span className="font-semibold">+44</span>
                            </div>
                            <input
                                type="tel"
                                value={phoneNumber}
                                inputMode="tel"
                                pattern="[0-9 ]*"
                                onChange={(e) => handlePhoneChange(e.target.value)}
                                placeholder="e.g. 07123456789"
                                className="flex-1 min-w-0 px-4 py-2 mr-1 border rounded-md focus:outline-none focus:ring focus:ring-primary placeholder:text-sm"
                            />
                        </div>
                        <div className={`mt-2 overflow-hidden transition-all duration-300 ${phoneNumber ? 'max-h-6' : 'max-h-0'}`}>
                            <div className={`text-sm transition-opacity duration-300 ${phoneNumber ? 'opacity-100' : 'opacity-0'} ${isPhoneValid ? 'text-green-600' : 'text-gray-500'}`}>
                                {isPhoneValid ? '✔ Phone number valid' : '✘ Phone number not valid'}
                            </div>
                        </div>
                    </div>
                    <button
                        id="send-code-button"
                        onClick={handleSendCode}
                        disabled={!isPhoneValid || isSending}
                        className={`w-full font-semibold py-2 rounded-lg transition duration-100 mb-3 ${!isPhoneValid || isSending ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-primary text-white hover:bg-primary/90'}`}
                    >
                        {isSending ? (
                            <div className="flex items-center justify-center">
                                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                                Sending...
                            </div>
                        ) : (
                            'Send Verification Code'
                        )}
                    </button>
                </div>
            ) : (
                <div>
                    <div className="mb-4">
                        <OtpInput label="Verification Code" value={verificationCode} onChange={setVerificationCode} autoFocus />
                    </div>
                    <button
                        onClick={handleVerifySms}
                        disabled={(verificationCode || '').length !== 6}
                        className={`w-full font-semibold py-2 rounded-lg transition duration-100 mb-3 ${(verificationCode || '').length !== 6 ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-primary text-white hover:bg-primary/90'}`}
                    >
                        Verify Code
                    </button>
                    <button
                        type="button"
                        onClick={handleResendCode}
                        disabled={resendSecondsLeft > 0 || isResending}
                        id="resend-code-button"
                        className={`w-full font-semibold bg-white text-primary py-2 rounded-lg border border-primary hover:bg-primary/5 transition duration-100 disabled:text-gray-400 disabled:border-gray-300 disabled:bg-transparent mb-3 relative`}
                    >
                        <span className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${resendJustSent ? 'opacity-100' : 'opacity-0'}`}>
                            ✔ Code sent
                        </span>
                        <span className={`transition-opacity duration-300 ${resendJustSent ? 'opacity-0' : 'opacity-100'}`}>
                            {`Resend Code${resendSecondsLeft > 0 ? ` (${resendSecondsLeft}s)` : ''}`}
                        </span>
                    </button>
                </div>
            )}
        </div>
    );

    return (
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundImage: getBackgroundGradient() }}>
            <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl ring-1 ring-black/5 px-8 pb-8 mx-4">
                <div className="w-full">
                    {showEnvBanner && (
                        <div className={`-mx-8 mb-10 px-8 py-3 border font-semibold text-center rounded-t-2xl
                          ${import.meta.env.VITE_ENVIRONMENT === 'development' ? 'bg-yellow-200 text-yellow-900 border-yellow-300' : 'bg-blue-200 text-blue-900 border-blue-300'}
                        `}>
                            <div className="flex items-center justify-center gap-2">
                                <span>
                                    {import.meta.env.VITE_ENVIRONMENT === 'development' ? 'Development Environment' : 'Testing / Training Environment'}
                                </span>
                            </div>
                        </div>
                    )}
                    <img src="/td_logo_full_2025.png" alt="Logo" className={`mx-auto mb-8 ${!showEnvBanner ? 'mt-10' : ''}`} style={{ width: '220px', height: 'auto' }} />
                    <h2 className="text-2xl font-bold mb-2 text-center">Two‑Factor Authentication</h2>

                    {/* Subtitle. Production is authenticator-only; dev/staging offers a
                        choice, so the copy differs by env + step. */}
                    <div className="text-gray-600 text-sm mb-4 text-center space-y-1">
                        {!allowSms && (
                            <p>
                                For providers, 2FA is required. Set up an authenticator app
                                (e.g. Google or Microsoft Authenticator) to protect your account.
                            </p>
                        )}
                        {allowSms && !selectedMethod && (
                            <p>For providers, 2FA is required. Choose how you would like to receive your sign-in codes below.</p>
                        )}
                    </div>

                    {/* Production: authenticator-only. No picker, no method label. */}
                    {!allowSms && renderTotpEnrollment()}

                    {/* Dev/staging step 1 — method picker (no intro, no skip). */}
                    {allowSms && !selectedMethod && (
                        <MfaMethodSelector selectedMethod={null} onSelect={handleSelectMethod} disabled={false} />
                    )}

                    {/* Dev/staging step 2 — focused enrollment for the chosen method. */}
                    {allowSms && selectedMethod && (
                        <>
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-sm font-semibold text-gray-900">
                                    {selectedMethod === 'totp' ? 'Authenticator App' : 'SMS Text'}
                                </span>
                                {!isMethodLocked && (
                                    <button
                                        type="button"
                                        onClick={handleChangeMethod}
                                        className="text-xs font-medium text-primary hover:text-primary/80 underline"
                                    >
                                        Change method
                                    </button>
                                )}
                            </div>
                            <div
                                className="overflow-hidden transition-[height,opacity] duration-300 ease-out"
                                style={{ height: panelHeight || 'auto', opacity: 1 }}
                            >
                                <div ref={panelContentRef}>
                                    {selectedMethod === 'sms' && renderSmsEnrollment()}
                                    {selectedMethod === 'totp' && <div className="animate-fadeIn">{renderTotpEnrollment()}</div>}
                                </div>
                            </div>
                        </>
                    )}

                    {/* Error banner */}
                    <div className={`transition-all duration-300 overflow-hidden ${error ? 'max-h-24 mb-0' : 'max-h-0 mb-0'}`} role="alert">
                        <div className={`bg-red-100 border border-red-400 text-red-700 px-4 py-3 text-sm rounded-lg relative ${error ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}>
                            <span className="block sm:inline">{error}{errorCode ? ` (Code: ${errorCode})` : ''}</span>
                        </div>
                    </div>

                    <hr className="border-gray-200 mt-2 mb-2" />
                    <button
                        onClick={handleSignOut}
                        className="w-full text-gray-500 text-sm font-semibold hover:text-gray-700 transition duration-100 mt-2"
                    >
                        Sign out instead
                    </button>

                    {/* Support footer */}
                    <div className="mt-6 text-center">
                        {(() => {
                            const subject = 'Reporting Issue with ThinkDivergent - Set Up MFA';
                            const now = new Date();
                            const day = now.getDate();
                            const sfx = (d) => {
                                if (d % 100 >= 11 && d % 100 <= 13) return 'th';
                                switch (d % 10) { case 1: return 'st'; case 2: return 'nd'; case 3: return 'rd'; default: return 'th'; }
                            };
                            const easyTime = `${day}${sfx(day)} ${now.toLocaleString('en-GB', { month: 'long' })} ${now.getFullYear()} at ${now.toLocaleString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
                            const isoTime = now.toISOString();
                            const lines = [
                                'To ThinkDivergent Support Team,',
                                '',
                                'I would like to report an issue on the platform.',
                                '',
                                '[Please describe briefly what you were doing and what happened]',
                                '',
                                'Helpful Information:',
                                `- Screen: ${typeof window !== 'undefined' ? window.location.pathname : ''}`,
                                `- URL: ${typeof window !== 'undefined' ? window.location.href : ''}`,
                                `- Time: ${easyTime} (${isoTime})`,
                                auth?.currentUser?.uid ? `- User ID: ${auth.currentUser.uid}` : undefined,
                                supportOrg ? `- Organisation: ${supportOrg}` : undefined,
                                errorCode ? `- Support/Error code: ${errorCode}` : undefined,
                                error ? `- Message: ${error}` : undefined,
                                `- Browser: ${typeof navigator !== 'undefined' ? navigator.userAgent : ''}`
                            ].filter(v => v != null).join('\n');
                            const href = `mailto:support@thinkdivergent.co.uk?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines)}`;
                            return (
                                <p className="text-xs text-gray-500">
                                    Need help? Contact your organisation administrator or {''}
                                    <a href={href} className="underline text-primary">report an issue</a>.
                                </p>
                            );
                        })()}
                    </div>
                </div>
            </div>
        </div>
    );
}
